import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { SEO } from '../../utils/seo';

export default function CoursesPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Load the widget script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://procourses.co.uk/javascripts/widgets.js?v=20230502';
    
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    }

    return () => {
      // Cleanup script on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
    "description": "Professional training courses in security, safety, compliance and risk management delivered through accredited instructor network",
    "areaServed": {
      "@type": "Place",
      "name": "United Kingdom"
    }
  };

  const trainingAreas = [
    'First Aid at Work (Full, Requalification & Emergency)',
    'AED & Bleed Control Training',
    'Paediatric First Aid',
    'Basic Life Support',
    'Fire Safety / Fire Marshal',
    'Manual Handling',
    'Workplace Risk Awareness',
    'Lone Working Safety',
    'Mental Health Awareness',
    'Safeguarding (Adult & Child)',
    'Stress & Resilience Workshops',
    'Dementia & Vulnerability Awareness'
  ];

  const technicalServices = [
    'Electrical safety training',
    'Technical consultancy',
    'CCTV, Fire & Access System support',
    'Solar PV / EV advisory',
    'Infrastructure works (fencing, trenching, cable runs, welding and reinstatement)'
  ];

  return (
    <>
      <SEO
        title="Training Courses - Professional Security & Safety Training - LEWIS"
        description="Browse our comprehensive range of professional training courses delivered through accredited instructor network. Onsite, classroom and online training across the UK."
        keywords="training courses, security courses, safety training, compliance training, professional development, accredited courses"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar isScrolled={isScrolled} />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 bg-gradient-to-br from-red-600 to-red-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h6 className="text-red-100 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Professional Development</h6>
              <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 leading-tight font-poppins">
                Training Courses
              </h1>
              <p className="text-lg text-red-50 leading-relaxed font-inter">
                Browse our comprehensive range of professional training courses delivered through our accredited instructor network across the UK.
              </p>
            </div>
          </div>
        </section>

        {/* Training Delivery Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto mb-16">
              <div className="bg-gray-50 rounded-2xl p-10 border border-gray-200">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-2xl text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Training Delivery – Partner Instructor Network</h3>
                    <p className="text-base text-gray-700 leading-relaxed font-inter mb-4">
                      Training is delivered through a combination of accredited instructors, awarding-body partners and specialist trainers. This allows us to provide onsite, classroom and online training across the UK, without clients being limited to a single venue or provider.
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed font-inter">
                      Online courses are also available via our ProTrainings integration.
                    </p>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Key Training Areas</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {trainingAreas.map((area, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <i className="ri-check-line text-red-600 mt-1 flex-shrink-0"></i>
                        <span className="text-sm text-gray-700 font-inter">{area}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Services Section */}
            <div className="max-w-4xl mx-auto mb-16">
              <div className="bg-gray-50 rounded-2xl p-10 border border-gray-200">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-2xl text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Technical / Electrical Services – Specialist Partner Delivery</h3>
                    <p className="text-base text-gray-700 leading-relaxed font-inter">
                      Electrical and technical services are delivered by qualified specialists with BS 7671 (18th Edition) capability, providing installation, testing and compliance support in line with current British Standards.
                    </p>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Technical Services Coverage</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {technicalServices.map((service, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <i className="ri-check-line text-red-600 mt-1 flex-shrink-0"></i>
                        <span className="text-sm text-gray-700 font-inter">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Widget Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 font-poppins">Browse Available Courses</h2>
              <p className="text-base text-gray-600 font-inter">
                Select from our range of accredited training courses delivered through our approved instructor network.
              </p>
            </div>
            {/* Widget Container */}
            <div className="pt-online-courses" data-affiliate-id="71045" data-course-collection="48"></div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-red-50 rounded-2xl p-10 border border-red-100">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-customer-service-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">
                  Need Help Choosing a Course?
                </h3>
                <p className="text-base text-gray-700 leading-relaxed font-inter mb-6">
                  Our team can help you select the right training courses for your team and operational requirements. Contact us for guidance on course selection, scheduling and group bookings.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="tel:07834561015"
                    className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-inter"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    07834 561015
                  </a>
                  <a
                    href="mailto:info@lewissecurity.co.uk"
                    className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-red-600 font-semibold rounded-lg border-2 border-red-600 hover:bg-red-50 transition-all duration-300 whitespace-nowrap cursor-pointer font-inter"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    Email Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
